import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { getDevices, createDevice, updateDevice, deleteDevice, type DeviceSortBy, type SortDir } from '../api/devices-api';
import { getUnits } from '@/features/units/api/units-api';
import { useDataCache, makePaginatedCacheKey } from '@/store/data-cache-context';
import { useOrg } from '@/store/org-context';
import { useDebounce } from '@/lib/utils';
import { usePagePolling, POLL_INTERVAL_LIST_MS } from '@/lib/use-page-polling';
import type { Device, CreateDeviceRequest, PaginatedResponse, Unit } from '@/types/api';

const PER_PAGE = 15;
const ALL_UNITS_KEY = makePaginatedCacheKey({});

// Default to last-seen-desc so the busiest devices surface first when
// Mike opens /devices for org-wide triage. URL-driven so the choice
// survives reload and back/forward navigation.
const DEFAULT_SORT_BY: DeviceSortBy = 'lastSeen';
const DEFAULT_SORT_DIR: SortDir = 'desc';

const SORT_BY_VALUES: DeviceSortBy[] = ['unit', 'createdAt', 'lastSeen', 'propertyName'];
const SORT_DIR_VALUES: SortDir[] = ['asc', 'desc'];

function parseSortBy(raw: string | null): DeviceSortBy {
  return (raw && (SORT_BY_VALUES as readonly string[]).includes(raw))
    ? (raw as DeviceSortBy)
    : DEFAULT_SORT_BY;
}
function parseSortDir(raw: string | null): SortDir {
  return (raw && (SORT_DIR_VALUES as readonly string[]).includes(raw))
    ? (raw as SortDir)
    : DEFAULT_SORT_DIR;
}

export function useDevicesPage() {
  const {
    invalidate,
    getUnitsPage: getCachedUnitsPage,
    setUnitsPage: setCachedUnitsPage,
  } = useDataCache();
  const { activeOrgId } = useOrg();

  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Math.max(1, Number(searchParams.get('page') ?? 1));
  const [searchInput, setSearchInput] = useState(searchParams.get('q') ?? '');
  const debouncedSearch = useDebounce(searchInput, 300);
  const sortBy = parseSortBy(searchParams.get('sortBy'));
  const sortDir = parseSortDir(searchParams.get('sortDir'));

  const [devicesResult, setDevicesResult] = useState<PaginatedResponse<Device> | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  // `loading` is *only* the first-paint blocker. Search refetches and
  // poll ticks set `refreshing` instead so the table stays mounted with
  // the prior result on screen — previously every keystroke triggered
  // a full LoadingPage swap, which felt like an "auto-refresh" to users
  // even though the upstream call was already debounced.
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [deletingDevice, setDeletingDevice] = useState<Device | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Multi-select state for the bulk-action bar. Cleared on any mutation
  // (single delete, page change, search change) so a stale selection
  // doesn't reference rows the user can't see anymore.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Reset to page 1 when search changes. Also clear any active
  // multi-selection — a selection from page 1 doesn't make sense once
  // the user has narrowed the list.
  useEffect(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('page');
      if (debouncedSearch) next.set('q', debouncedSearch);
      else next.delete('q');
      return next;
    }, { replace: true });
    setSelectedIds(new Set());
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear selection on page change too — the visible row set is now
  // different so the bulk-action bar's "X selected" would otherwise
  // refer to off-screen rows the user can't audit.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [currentPage]);

  useEffect(() => {
    loadDevices();
  }, [currentPage, debouncedSearch, sortBy, sortDir, activeOrgId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Background poll on the standardized list-page cadence. Silent so the
  // table doesn't flash a spinner between polls; React reconciliation
  // handles the DOM diff when the fetched list is identical to what's
  // already rendered.
  usePagePolling(() => { void loadDevices({ silent: true }); }, POLL_INTERVAL_LIST_MS);

  useEffect(() => {
    loadUnits();
  }, [activeOrgId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Always fetch fresh. silent:true is used by the 5s polling tick so
  // neither flag flips. Otherwise: the *first* call (devicesResult ===
  // null) flips `loading` for the page-level spinner; subsequent calls
  // (search/pagination) only flip `refreshing` so the prior result stays
  // visible until the new one lands. The paginated cache was pulled
  // entirely — it caused too many stale-data bugs on a simple admin list.
  // Sibling views that DO consult their own caches still get invalidated
  // on mutations below.
  async function loadDevices({ silent = false }: { silent?: boolean } = {}) {
    const isFirstLoad = !devicesResult;
    if (!silent) {
      if (isFirstLoad) setLoading(true);
      else setRefreshing(true);
    }
    try {
      const result = await getDevices({ search: debouncedSearch || undefined, page: currentPage, perPage: PER_PAGE, sortBy, sortDir });
      setDevicesResult(result);
    } catch (error) {
      if (!silent) console.error('Failed to load devices:', error);
      // On background polls, swallow transient fetch failures — next tick
      // retries. A visible error flag would flap on spotty connections.
    } finally {
      if (!silent) {
        if (isFirstLoad) setLoading(false);
        else setRefreshing(false);
      }
    }
  }

  async function loadUnits() {
    const cached = getCachedUnitsPage(ALL_UNITS_KEY);
    if (cached) {
      setUnits(cached.items);
      return;
    }
    try {
      const result = await getUnits();
      setCachedUnitsPage(ALL_UNITS_KEY, result);
      setUnits(result.items);
    } catch (error) {
      console.error('Failed to load units:', error);
    }
  }

  function handlePageChange(page: number) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (page > 1) next.set('page', String(page));
      else next.delete('page');
      return next;
    }, { replace: true });
  }

  // Sort change resets to page 1 — paging in the new ordering is the
  // only useful behavior. Defaults are dropped from the URL to keep
  // shareable links clean.
  function handleSortChange(nextBy: DeviceSortBy, nextDir: SortDir) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (nextBy === DEFAULT_SORT_BY) next.delete('sortBy');
      else next.set('sortBy', nextBy);
      if (nextDir === DEFAULT_SORT_DIR) next.delete('sortDir');
      else next.set('sortDir', nextDir);
      next.delete('page');
      return next;
    }, { replace: true });
  }

  function handleAddDevice() {
    setEditingDevice(null);
    setIsModalOpen(true);
  }

  function handleEditDevice(device: Device) {
    setEditingDevice(device);
    setIsModalOpen(true);
  }

  async function handleDeleteConfirm() {
    if (!deletingDevice) return;
    const deviceToDelete = deletingDevice;
    setIsDeleting(true);
    setDeletingDevice(null);
    try {
      await deleteDevice(deviceToDelete.id);
      invalidate(['devices', 'units', 'properties', 'meter-readings', 'alerts']);
      await loadDevices();
    } catch (error) {
      console.error('Failed to delete device:', error);
    } finally {
      setIsDeleting(false);
    }
  }

  function toggleSelection(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Header checkbox: select all currently-visible devices, or clear if
  // already all-selected. Constrained to the visible page so the user
  // can audit exactly what they're acting on.
  function toggleSelectAllVisible() {
    setSelectedIds((prev) => {
      const visibleIds = new Set(devicesResult?.items.map((d) => d.id) ?? []);
      const allSelected = visibleIds.size > 0 && [...visibleIds].every((id) => prev.has(id));
      if (allSelected) {
        // Drop just the visible IDs from the selection — preserves any
        // off-page selections (defensive; we currently clear those on
        // page change but the math is the same either way).
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...visibleIds]);
    });
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  // Sequential delete loop. Hits the same endpoint single-delete uses,
  // so AWS IoT deregister + audit log fire per device. Sequential
  // (not Promise.all) keeps the AWS IoT API from getting hammered with
  // bulk deletes and gives consistent error semantics — first failure
  // stops the loop. Caller can retry the remaining selection.
  async function bulkDeleteSelected(): Promise<{ ok: number; failed: number }> {
    const ids = [...selectedIds];
    if (ids.length === 0) return { ok: 0, failed: 0 };
    setBulkDeleting(true);
    let ok = 0;
    let failed = 0;
    try {
      for (const id of ids) {
        try {
          await deleteDevice(id);
          ok++;
        } catch (error) {
          failed++;
          console.error(`Failed to delete device ${id}:`, error);
        }
      }
      invalidate(['devices', 'units', 'properties', 'meter-readings', 'alerts']);
      await loadDevices();
    } finally {
      setBulkDeleting(false);
      setSelectedIds(new Set());
    }
    return { ok, failed };
  }

  async function handleSaveDevice(data: CreateDeviceRequest) {
    if (editingDevice) {
      setIsModalOpen(false);
      try {
        await updateDevice(editingDevice.id, data);
        invalidate(['devices']);
        await loadDevices();
      } catch (error) {
        console.error('Failed to update device:', error);
      }
    } else {
      setIsModalOpen(false);
      try {
        await createDevice(data);
        invalidate(['devices']);
        await loadDevices();
      } catch (error) {
        console.error('Failed to update device:', error);
      }
    }
  }

  const devices = devicesResult?.items ?? [];
  const totalCount = devicesResult?.totalCount ?? 0;
  const totalPages = devicesResult?.totalPages ?? 1;

  return {
    devices,
    units,
    loading,
    refreshing,
    debouncedSearch,
    selectedIds,
    bulkDeleting,
    toggleSelection,
    toggleSelectAllVisible,
    clearSelection,
    bulkDeleteSelected,
    totalCount,
    totalPages,
    currentPage,
    searchInput,
    setSearchInput,
    handlePageChange,
    sortBy,
    sortDir,
    handleSortChange,
    isModalOpen,
    setIsModalOpen,
    editingDevice,
    deletingDevice,
    setDeletingDevice,
    isDeleting,
    handleAddDevice,
    handleEditDevice,
    handleDeleteConfirm,
    handleSaveDevice,
  };
}
